import { useEffect } from 'react'
import { isKioskIdle } from '@/lib/kiosk-session'
import { useKioskStore } from '@/stores/kiosk-store'

const CHECK_INTERVAL = 5 * 60 * 1000 // check every 5 min
const INDEX_URL = '/'

/**
 * Polls `/` for a changed main JS bundle hash and auto-reloads the kiosk
 * when a new version is deployed. Vite emits hashed asset URLs into
 * index.html (e.g. `/assets/index-B5ORH5N5.js`), so we just compare the
 * filename in the served HTML against the one in the running document.
 *
 * Only reloads when the kiosk is idle (on the start screen, no active
 * order) so we don't interrupt a customer mid-order. Staff can still
 * force-update manually via the admin modal.
 *
 * Requires CloudFront to serve `/index.html` with a short TTL (or
 * `no-cache`); hashed assets stay `immutable` as usual.
 */

const ASSET_RE = /\/assets\/index-[A-Za-z0-9_-]+\.js/

function extractBundle(html: string): string | null {
  const m = html.match(ASSET_RE)
  return m ? m[0] : null
}

function currentBundle(): string | null {
  if (typeof document === 'undefined') return null
  const scripts = document.querySelectorAll<HTMLScriptElement>('script[src]')
  for (const s of scripts) {
    const m = s.src.match(ASSET_RE)
    if (m) return m[0]
  }
  return null
}

export function useAutoUpdate() {
  useEffect(() => {
    let cancelled = false
    let checking = false
    let pendingBundle: string | null = null
    const running = currentBundle()
    if (!running) return // dev mode (no hashed bundle) — nothing to compare

    const applyPending = () => {
      if (cancelled || !pendingBundle || document.visibilityState !== 'visible' || !isKioskIdle(useKioskStore.getState())) return
      // Block single-menu auto-advance before initiating navigation.
      useKioskStore.setState({ refreshing: true })
      forceUpdate().catch(() => useKioskStore.setState({ refreshing: false }))
    }

    const check = async () => {
      if (checking || cancelled || document.visibilityState !== 'visible') return
      checking = true
      try {
        const res = await fetch(`${INDEX_URL}?t=${Date.now()}`, {
          cache: 'no-store',
        })
        if (!res.ok) return
        const html = await res.text()
        const served = extractBundle(html)
        if (cancelled || !served) return
        pendingBundle = served === running ? null : served
        applyPending()
      } catch {
        /* network error — will retry next interval */
      } finally {
        checking = false
      }
    }

    // Subscribe synchronously so pending updates win the race with automatic
    // menu selection when a single-menu kiosk returns to its start screen.
    const unsubscribe = useKioskStore.subscribe((state, previous) => {
      if (isKioskIdle(state) && !isKioskIdle(previous)) {
        applyPending()
        if (!useKioskStore.getState().refreshing) check()
      }
    })
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        applyPending()
        check()
      }
    }
    check()
    const id = setInterval(check, CHECK_INTERVAL)
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      cancelled = true
      clearInterval(id)
      unsubscribe()
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])
}

export async function forceUpdate() {
  // KRITISKT: location.reload() respekterar browser-HTTP-cachen. Om HTML:en
  // är cachad serveras den och laddar samma gamla JS-bundle (hashade filer
  // är `immutable`). Vi tvingar fram en färsk request genom att byta URL
  // (add/update en query-param) — då matchar ingen cache-nyckel och browser
  // hämtar allt från nätverket.
  const url = new URL(window.location.href)
  url.searchParams.set('_v', Date.now().toString())
  window.location.replace(url.toString())
}
